import { type DateTime, NaiveDate, type Utc } from "@tento-chrono";
import type { Option, RowLocalId } from "@tento-lona/sheets";
import type { CellEntry } from "@tento-lona/sheets";
import { CellId } from "@tento-lona/sheets";

/**
 * Where a cell write lands on a held row — the address of every cell write
 * verb of the row's narrowed facades (`row.writableSingle()` /
 * `row.writableMulti()`: `write`, `writeDurable`, `moveCell`,
 * `replaceCell`, `clear`, `clearDurable`). The row routes the write to the
 * store its address names; the caller never picks a store.
 *
 * - `date`: the row's date-keyed cell for a day / week / month / year column
 *   (`CellTarget.date(day)`, a `NaiveDate` or a `NaiveDate.Partial`) — the
 *   column's one cell on a single-cardinality row. A multi-cardinality row
 *   (tasks, events, calendar lists, scheduling links) tells its cells apart
 *   by id, so `date` names none of them: every verb rejects it with
 *   `CellWriteError` (outcome `unaddressable`) before anything is sent. Hold
 *   the cell to change it (`CellTarget.cell`, `CellTarget.keyed`); add one
 *   with `add`.
 * - `add`: a new date-keyed cell in column `date` of a multi-cardinality row,
 *   beside the ones already there (`CellTarget.add(day)`). The server mints
 *   its id and the row takes the cell from the server's answer, so it has no
 *   durable form and names no existing cell. On a single-cardinality row
 *   (one cell per column: write `date`) every verb rejects it with
 *   `CellWriteError` (outcome `unaddressable`), nothing sent.
 * - `add-with-id`: a new date-keyed cell in column `date` of a
 *   multi-cardinality row whose id the caller chooses
 *   (`CellTarget.addWithId(date, id)`: a task's id). It names no existing
 *   cell, so it names no row. Like `add`, only a multi-cardinality row
 *   takes it; a single-cardinality row refuses it (`unaddressable`),
 *   nothing sent.
 * - `dated-cell` / `timepoint-cell`: a cell the caller holds, as a read
 *   handed it out (`CellTarget.cell(entry)`, an entry of `row.cells(range)`
 *   or of the single facade's reads, `row.single()?.peek(day)`): a
 *   date-keyed cell (its `id`, when it has one, is the upsert key; a
 *   single-cardinality row's cell has none, and the row refuses one that
 *   names an id — `unaddressable`, nothing sent) or a timepoint cell,
 *   named by its `id` alone — the row
 *   reads the cell's current range and value from its own store, so a
 *   stale entry never re-posts an old value or restores an old range; a
 *   `write` or `move` of an id the row does not hold is refused
 *   (`unaddressable`), nothing sent.
 *   `CellTarget.cell` returns the kind the entry is (its `kind`), so a verb
 *   that takes only one of them rejects the other at compile time. The
 *   entry carries the row that read it (`entry.row`): every verb of another
 *   row rejects it with `CellWriteError` (outcome `unaddressable`) before
 *   anything is sent. A cell known only by its id (a tool's argument, a
 *   stored reference) is addressed with `CellTarget.keyed(row, date, id)`
 *   or `CellTarget.timepoint(row, id)`, which name the row that holds it
 *   (`row.id`) and are refused by any other row the same way.
 * - `range`: a new timepoint cell spanning `range`
 *   (`CellTarget.range(range)`). The server mints its id, so it has no
 *   durable form.
 *
 * Each verb takes only the addresses it can honour, and the row's
 * cardinality is part of that: a `date` names a single-cardinality row's
 * cell and an `add` / `add-with-id` a new cell of a multi-cardinality row.
 * A row has no write verb of its own: narrow it first —
 * `row.writableSingle()` (`SheetRow.SingleCells`: `write` takes
 * `CellTarget.Single` — `date`, a held id-less date-keyed cell, a `range`
 * or a held timepoint cell; an id-keyed address does not compile) or
 * `row.writableMulti()` (`SheetRow.MultiCells`: `write` takes
 * `CellTarget.Multi` — `add`, `addWithId`, a held cell or a `range`); each
 * is `null` on a row of the other cardinality and on a row whose cells are
 * computed, so check it before writing, and a mismatched target — or a
 * write to a computed row — does not compile. On either facade
 * `writeDurable` / `clearDurable` take a date-keyed address, `clear` a held
 * cell (or the column's), and `moveCell` / `replaceCell` a held timepoint
 * cell (`CellTarget.OnTimepointCell`).
 */
export type CellTarget =
  | CellTarget.OnDate
  | CellTarget.OnAdd
  | CellTarget.OnAddWithId
  | CellTarget.OnDatedCell
  | CellTarget.OnTimepointCell
  | CellTarget.OnRange;

/**
 * Types and values that go with `CellTarget`: `OnDate`, `OnAdd`, `OnAddWithId`,
 * `OnDatedCell`, `OnTimepointCell`, `OnCell`, and 17 more.
 */
export namespace CellTarget {
  /** The row's date-keyed cell for `date` (a single-cardinality row's). */
  export type OnDate = { readonly kind: "date"; readonly date: NaiveDate.Partial };
  /** A new date-keyed cell in column `date` of a multi-cardinality row. */
  export type OnAdd = { readonly kind: "add"; readonly date: NaiveDate.Partial };
  /** A new date-keyed cell in column `date` of a multi-cardinality row,
   *  whose id the caller chooses (`addWithId`). */
  export type OnAddWithId = { readonly kind: "add-with-id"; readonly date: NaiveDate.Partial; readonly id: CellId };
  /** A date-keyed cell the caller holds. `Id` narrows its id: `null` for
   *  the column's one cell of a single-cardinality row, a `CellId` for an
   *  id-keyed cell of a multi-cardinality row (`keyed`). */
  export type OnDatedCell<Id extends Option<CellId> = Option<CellId>> = {
    readonly kind: "dated-cell";
    readonly cell: DatedCell<Id>;
  };
  /** A timepoint cell the caller holds. */
  export type OnTimepointCell = { readonly kind: "timepoint-cell"; readonly cell: TimepointCell };
  /** A cell the caller holds, of either kind (`CellTarget.cell`). */
  export type OnCell = OnDatedCell | OnTimepointCell;
  /** A new timepoint cell spanning `range`. */
  export type OnRange = { readonly kind: "range"; readonly range: DateTime.Range<Utc> };
  /** A single-cardinality row's write address (`SheetRow.SingleCells.write`):
   *  its column's cell (`Dated`), a new timepoint cell (`range`) or a held
   *  timepoint cell — a single-cardinality row holds timepoint cells too
   *  (a sticker row's). */
  export type Single = Dated | OnTimepointCell | OnRange;
  /** A single-cardinality row's date-keyed address (`SheetRow.SingleCells`):
   *  the column's cell, or a held id-less date-keyed cell (the column's one
   *  cell, as the single facade's reads hand it out). An id names a cell of
   *  a multi-cardinality row, so no id-keyed address is one: `keyed` and
   *  `addWithId` do not compile here. Every verb of the single facade takes
   *  it (it has a durable form: its id is not minted). */
  export type Dated = OnDate | OnDatedCell<null>;
  /** A multi-cardinality row's write address (`SheetRow.MultiCells.write`):
   *  a new date-keyed cell (`add`, or `addWithId` naming its id), a held
   *  cell, or a new timepoint cell. */
  export type Multi = OnAdd | OnAddWithId | OnCell | OnRange;
  /** A multi-cardinality row's durable date-keyed address
   *  (`SheetRow.MultiCells.writeDurable` / `clearDurable`): a held
   *  date-keyed cell, or a new one whose id the caller chooses (its id is
   *  not minted, so it has a durable form). */
  export type MultiDated = OnDatedCell | OnAddWithId;
  /** Every date-keyed address, of either cardinality — what the host's
   *  local paint verbs take (`platformOf(handle).local.writeCell` /
   *  `clearCell`, `platformOf(client).local`): the row's cardinality is
   *  checked when it is known (an id on a single-cardinality row, an
   *  id-less `date` on a multi-cardinality one: `unaddressable`). */
  export type DateKeyed = OnDate | OnDatedCell | OnAddWithId;
  /** A cell as a row's read hands it out: date-keyed or timepoint (`SheetRow.Cell`). */
  export type Held = HeldDateCell | HeldTimepointCell;

  /** A date-keyed cell as a row's read hands it out (`SheetRow.DateCell`): the
   *  model tier's `CellEntry` (its column `date`, its `id` on a
   *  multi-cardinality row, else `null`) and the row that read it. `Id`
   *  narrows the id: the single facade's reads hand out `HeldDateCell<null>`
   *  (the column's one cell, which has no id). Declared here, with the
   *  addresses that name it, so the cell modules need nothing of the row. */
  export type HeldDateCell<Id extends Option<CellId> = Option<CellId>> = CellEntry & {
    readonly kind: "date";
    /** The row that read it. */
    readonly row: RowLocalId;
    /** Its id: `null` for the column's one cell of a single-cardinality row. */
    readonly id: Id;
  };

  /** A timepoint cell as a row's read hands it out (`SheetRow.TimepointCell`):
   *  the model tier's `CellEntry.Timepoint` (its `id`, its range `dtr`) and
   *  the row that read it. */
  export type HeldTimepointCell = CellEntry.Timepoint & {
    readonly kind: "timepoint";
    /** The row that read it. */
    readonly row: RowLocalId;
  };

  /**
   * A held date-keyed cell as a target names it: the row that holds it (the
   * row that read it, or the row `keyed` names), its column, and its id
   * (`null`: the column's one cell of a single-cardinality row; a `CellId`:
   * an id-keyed cell of a multi-cardinality row). `Id` narrows the id.
   */
  export type DatedCell<Id extends Option<CellId> = Option<CellId>> = {
    readonly row: RowLocalId;
    readonly date: NaiveDate.Partial;
    readonly id: Id;
  };

  /**
   * A held timepoint cell as a target names it: the row that holds it (the
   * row that read it, or the row `timepoint` names) and its id. Nothing
   * else: the verb reads the cell's current range and value from the row by
   * the id (a `move` re-posts that value at the new range), so a caller
   * never restates a cell's content.
   */
  export type TimepointCell = {
    readonly row: RowLocalId;
    readonly id: CellId;
  };

  /**
   * The row's date-keyed cell for `date` (a day, or a week / month / year
   * column) — the column's one cell of a single-cardinality row. A
   * multi-cardinality row refuses it (`unaddressable`): hold its cell, or
   * `add` one.
   */
  export function date(date: NaiveDate | NaiveDate.Partial): OnDate {
    return { kind: "date", date: partialOf(date) };
  }

  /**
   * A new date-keyed cell in column `date` of a multi-cardinality row (a
   * task, an all-day event), beside the cells already there. The server
   * mints its id; the row takes the new cell from the write's answer, as a
   * `range` write takes a new timepoint cell. A single-cardinality row
   * refuses it (`unaddressable`): write `date`.
   */
  export function add(date: NaiveDate | NaiveDate.Partial): OnAdd {
    return { kind: "add", date: partialOf(date) };
  }

  function partialOf(date: NaiveDate | NaiveDate.Partial): NaiveDate.Partial {
    return date instanceof NaiveDate.Partial ? date : NaiveDate.Partial.fromDate(date);
  }

  /** A cell the caller holds (read from the row), addressed as the kind it
   *  is: the column's one cell of a single-cardinality row (the single
   *  facade's reads, `id: null`) is a `Dated` address. */
  export function cell(cell: HeldTimepointCell): OnTimepointCell;
  export function cell(cell: HeldDateCell<null>): OnDatedCell<null>;
  export function cell(cell: HeldDateCell): OnDatedCell;
  export function cell(cell: Held): OnCell;
  export function cell(cell: Held): OnCell {
    return cell.kind === "timepoint"
      ? { kind: "timepoint-cell", cell }
      : { kind: "dated-cell", cell };
  }

  /**
   * The id-keyed date cell `id` in column `date` of row `row` (`row.id`),
   * known by its id and not read from the row (a tool's argument, a stored
   * reference). Bound to `row`: a verb of any other row refuses it with
   * `CellWriteError` (outcome `unaddressable`), nothing sent, as it refuses
   * an entry another row read. The server upserts the cell by its id, so a
   * write at another `date` moves it there. A cell whose id the caller
   * chooses and no row holds yet is `addWithId`. An id names a cell of a
   * multi-cardinality row: it is not a `Dated` address (the single
   * facade's), and the row's own verbs refuse it on a single-cardinality
   * row (`unaddressable`), nothing sent.
   */
  export function keyed(row: RowLocalId, date: NaiveDate | NaiveDate.Partial, id: CellId): OnDatedCell<CellId> {
    return { kind: "dated-cell", cell: { row, date: CellTarget.date(date).date, id } };
  }

  /**
   * A NEW date-keyed cell in column `date` of a multi-cardinality row whose
   * id the caller chooses (`id`: a task's id, a seeded cell's) — the create
   * a `keyed` address does not name. It names no existing cell, so it names
   * no row: the verb's row takes it. The server upserts by id, so an id a
   * row already holds is rewritten (at `date`). Only a multi-cardinality
   * row takes it (`row.writableMulti()`: `CellTarget.Multi`); a
   * single-cardinality row has one cell per column and refuses it
   * (`unaddressable`), nothing sent.
   */
  export function addWithId(date: NaiveDate | NaiveDate.Partial, id: CellId): OnAddWithId {
    return { kind: "add-with-id", date: CellTarget.date(date).date, id };
  }

  /**
   * The timepoint cell `id` of row `row` (`row.id`), known by its id and not
   * read from the row (an item a surface keeps of its own). Bound to `row`:
   * a verb of any other row refuses it (`unaddressable`), nothing sent. It
   * states neither range nor value: the verb reads both from the row's
   * store by the id, and refuses an id the row does not hold
   * (`unaddressable`).
   */
  export function timepoint(row: RowLocalId, id: CellId): OnTimepointCell {
    return { kind: "timepoint-cell", cell: { row, id } };
  }

  /** A new timepoint cell spanning `range`; the server mints its id. */
  export function range(range: DateTime.Range<Utc>): OnRange {
    return { kind: "range", range };
  }

  /** Whether a held cell is a timepoint cell (its `kind`). */
  export function isTimepoint(cell: Held): cell is HeldTimepointCell {
    return cell.kind === "timepoint";
  }
}
